import React, { useEffect, useState, memo, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Package, GraduationCap, Bus, Coffee, ChevronDown, X, Heart, Shield, BookOpen, Users, Globe, Camera, Star, Award, Crown } from 'lucide-react';
// --- Atoms ---
const OrnateDivider = ({
  className = ''
}: {
  className?: string;
}) => <div className={`flex items-center justify-center gap-3 ${className}`} data-id="element-7401">
    <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#D4AF37]/60" data-id="element-7402" />
    <svg width="28" height="14" viewBox="0 0 28 14" fill="none" data-id="element-7403">
      <path d="M14 0 L17 4 L24 5 L19 9 L20 14 L14 11 L8 14 L9 9 L4 5 L11 4 Z" fill="#D4AF37" data-id="element-7404" />
    </svg>
    <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#D4AF37]/60" data-id="element-7405" />
  </div>;
const CornerOrnaments = () => <>
    <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#D4AF37]/40 opacity-50" data-id="element-7406" />
    <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#D4AF37]/40 opacity-50" data-id="element-7407" />
    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#D4AF37]/40 opacity-50" data-id="element-7408" />
    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37]/40 opacity-50" data-id="element-7409" />
  </>;
const SectionHeader = ({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string;
}) => <div className="text-center mb-16" data-id="element-7410">
    <OrnateDivider className="mb-6" data-id="element-7411" />
    <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-7412">
      {title}
    </h2>
    {subtitle && <p className="text-[#FDFBF7]/60 text-sm max-w-2xl mx-auto" data-id="element-7413">{subtitle}</p>}
  </div>;
// --- Modals ---
const BuyChaaModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [amount, setAmount] = useState<string>('21');
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsSuccess(false);
      setIsSubmitting(false);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(onClose, 2500);
    }, 1500);
  };
  return <AnimatePresence data-id="element-7414">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-7415">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/90 backdrop-blur-sm" data-id="element-7416" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-md bg-[#131B2B] border border-[#D4AF37]/30 p-8 shadow-2xl" data-id="element-7417">
            <CornerOrnaments data-id="element-7418" />
            <button onClick={onClose} className="absolute top-4 right-4 text-[#FDFBF7]/40 hover:text-[#D4AF37] transition-colors" data-id="element-7419">
              <X className="w-5 h-5" data-id="element-7420" />
            </button>

            {isSuccess ? <div className="text-center py-8" data-id="element-7421">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/30" data-id="element-7422">
                  <Coffee className="w-8 h-8 text-[#D4AF37]" data-id="element-7423" />
                </div>
                <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-7424">
                  Thank You
                </h3>
                <p className="text-[#FDFBF7]/60 text-sm" data-id="element-7425">
                  Your support means the world to our team.
                </p>
              </div> : <form onSubmit={handleSubmit} data-id="element-7426">
                <div className="text-center mb-8" data-id="element-7427">
                  <Coffee className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" data-id="element-7428" />
                  <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-7429">
                    Buy Chaa for the Team
                  </h3>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-7430">
                    Support the artists, guides, and builders bringing the
                    Lahore Treasury to life.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4" data-id="element-7431">
                  {['11', '21', '51'].map(val => <button key={val} type="button" onClick={() => {
              setAmount(val);
              setCustomAmount('');
            }} className={`py-3 border text-sm transition-colors ${amount === val ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#D4AF37]/20 text-[#FDFBF7]/60 hover:border-[#D4AF37]/50'}`} data-id="element-7432">
                      ${val}
                    </button>)}
                </div>
                <div className="mb-6" data-id="element-7433">
                  <input type="number" placeholder="Custom Amount ($)" value={customAmount} onChange={e => {
              setCustomAmount(e.target.value);
              setAmount('custom');
            }} className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors" data-id="element-7434" />
                </div>
                <div className="mb-8" data-id="element-7435">
                  <textarea placeholder="Optional message to the team..." value={message} onChange={e => setMessage(e.target.value)} className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors h-24 resize-none" data-id="element-7436" />
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-sm hover:bg-[#E5C158] transition-colors disabled:opacity-50" data-id="element-7437">
                  {isSubmitting ? 'Processing...' : `Support with $${amount === 'custom' ? customAmount || '0' : amount}`}
                </button>
              </form>}
          </motion.div>
        </div>}
    </AnimatePresence>;
};
const SponsorInquiryModal = ({
  isOpen,
  onClose,
  defaultType = ''
}: {
  isOpen: boolean;
  onClose: () => void;
  defaultType?: string;
}) => {
  const [type, setType] = useState(defaultType);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setType(defaultType);
      setIsSuccess(false);
      setIsSubmitting(false);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, defaultType]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(onClose, 3000);
    }, 1500);
  };
  return <AnimatePresence data-id="element-7438">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-7439">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/90 backdrop-blur-sm" data-id="element-7440" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-lg bg-[#131B2B] border border-[#D4AF37]/30 p-8 shadow-2xl max-h-[90vh] overflow-y-auto scrollbar-hide" data-id="element-7441">
            <CornerOrnaments data-id="element-7442" />
            <button onClick={onClose} className="absolute top-4 right-4 text-[#FDFBF7]/40 hover:text-[#D4AF37] transition-colors" data-id="element-7443">
              <X className="w-5 h-5" data-id="element-7444" />
            </button>

            {isSuccess ? <div className="text-center py-12" data-id="element-7445">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/30" data-id="element-7446">
                  <Shield className="w-8 h-8 text-[#D4AF37]" data-id="element-7447" />
                </div>
                <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-7448">
                  Inquiry Received
                </h3>
                <p className="text-[#FDFBF7]/60 text-sm" data-id="element-7449">
                  A member of the Lahore Treasury team will contact you shortly.
                </p>
              </div> : <form onSubmit={handleSubmit} data-id="element-7450">
                <div className="text-center mb-8" data-id="element-7451">
                  <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-7452">
                    Become a Patron
                  </h3>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-7453">
                    Help open the Lahore Court to the next generation.
                  </p>
                </div>

                <div className="space-y-4 mb-8" data-id="element-7454">
                  <input required type="text" placeholder="Full Name" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors" data-id="element-7455" />
                  <input required type="email" placeholder="Email Address" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors" data-id="element-7456" />

                  <select value={type} onChange={e => setType(e.target.value)} required className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors appearance-none" data-id="element-7457">
                    <option value="" disabled data-id="element-7458">
                      Select Sponsorship Type
                    </option>
                    <option value="Student Access" data-id="element-7459">
                      Student Access ($25+)
                    </option>
                    <option value="School Bus" data-id="element-7460">School Bus ($1,500+)</option>
                    <option value="Collection" data-id="element-7461">
                      Collection Sponsor ($5,000+)
                    </option>
                    <option value="Treasury Night" data-id="element-7462">
                      Treasury Night Sponsor
                    </option>
                    <option value="Commemorative Coin" data-id="element-7463">
                      Commemorative Coin Sponsor
                    </option>
                    <option value="Founding Partner" data-id="element-7464">
                      Founding Partner ($10,000+)
                    </option>
                    <option value="Court Packet" data-id="element-7465">Buy Court Packets</option>
                    <option value="Other" data-id="element-7466">Other Contribution</option>
                  </select>

                  <select required className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors appearance-none" data-id="element-7467">
                    <option value="" disabled data-id="element-7468">
                      Budget Range
                    </option>
                    <option value="Under $500" data-id="element-7469">Under $500</option>
                    <option value="$500 - $2,500" data-id="element-7470">$500 - $2,500</option>
                    <option value="$2,500 - $10,000" data-id="element-7471">$2,500 - $10,000</option>
                    <option value="$10,000+" data-id="element-7472">$10,000+</option>
                  </select>

                  <textarea placeholder="Message or specific requests..." className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors h-24 resize-none" data-id="element-7473" />
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-sm hover:bg-[#E5C158] transition-colors disabled:opacity-50" data-id="element-7474">
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>}
          </motion.div>
        </div>}
    </AnimatePresence>;
};
// --- Main Page Component ---
export const SupportTheCourtPage = () => {
  const [isChaaModalOpen, setIsChaaModalOpen] = useState(false);
  const [isSponsorModalOpen, setIsSponsorModalOpen] = useState(false);
  const [sponsorModalType, setSponsorModalType] = useState('');
  const openSponsorModal = (type: string) => {
    setSponsorModalType(type);
    setIsSponsorModalOpen(true);
  };
  // Impact Calculator State
  const [impactAmount, setImpactAmount] = useState<number>(25);
  const [customImpact, setCustomImpact] = useState<string>('');
  const calculateImpact = (amount: number) => {
    const students = Math.floor(amount / 25);
    const packets = students;
    const buses = Math.floor(amount / 2000);
    return {
      students,
      packets,
      buses
    };
  };
  const currentImpact = calculateImpact(impactAmount);
  return <div className="min-h-screen w-full bg-[#0B0F19] text-[#FDFBF7] font-sans selection:bg-[#D4AF37] selection:text-[#0B0F19]" data-id="element-7475">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0B0F19]/90 border-b border-[#D4AF37]/20" data-id="element-7476">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between" data-id="element-7477">
          <Link to="/treasure" className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase flex items-center gap-1 transition-colors" data-id="element-7478">
            <ChevronLeft className="w-4 h-4" data-id="element-7479" /> Treasury Home
          </Link>
          <div className="font-serif text-xs sm:text-sm text-[#D4AF37] tracking-[0.25em] uppercase text-center" data-id="element-7480">
            Support the Court
          </div>
          <div className="w-24" data-id="element-7481" />
        </div>
      </header>

      {/* 1. Hero Section */}
      <section className="relative pt-24 pb-32 px-4 overflow-hidden" data-id="element-7482">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.08),_transparent_60%)]" data-id="element-7483" />
        <div className="max-w-5xl mx-auto relative z-10 text-center" data-id="element-7484">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-7485">
            <p className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-6" data-id="element-7486">
              Lahore Treasury
            </p>
            <h1 className="font-serif text-5xl sm:text-7xl text-[#FDFBF7] mb-8 leading-tight" data-id="element-7487">
              Help Open the <br className="hidden sm:block" data-id="element-7488" />
              <span className="text-[#D4AF37]" data-id="element-7489">Lahore Court</span>
            </h1>
            <p className="text-[#FDFBF7]/70 text-lg max-w-2xl mx-auto mb-12 leading-relaxed" data-id="element-7490">
              Lahore Treasury is more than a collectible platform. It is a
              cultural experience designed to bring the world of Maharaja Ranjit
              Singh’s Lahore Court to families, students, schools, and the next
              generation.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20" data-id="element-7491">
              <button onClick={() => openSponsorModal('Student Access')} className="w-full sm:w-auto px-8 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#E5C158] transition-colors" data-id="element-7492">
                Sponsor a Student
              </button>
              <button onClick={() => openSponsorModal('School Bus')} className="w-full sm:w-auto px-8 py-4 border border-[#D4AF37]/40 text-[#D4AF37] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#D4AF37]/10 transition-colors" data-id="element-7493">
                Sponsor a Bus
              </button>
              <button onClick={() => openSponsorModal('Founding Partner')} className="w-full sm:w-auto px-8 py-4 border border-[#FDFBF7]/20 text-[#FDFBF7] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#FDFBF7]/10 transition-colors" data-id="element-7494">
                Become a Patron
              </button>
            </div>

            {/* Hero Visual: Elegant Sealed Packet */}
            <div className="relative w-48 h-64 mx-auto group" data-id="element-7495">
              <div className="absolute inset-0 bg-gradient-to-br from-[#131B2B] to-[#0B0F19] border border-[#D4AF37]/40 shadow-[0_0_40px_rgba(212,175,55,0.1)] group-hover:border-[#D4AF37] group-hover:shadow-[0_0_60px_rgba(212,175,55,0.2)] transition-all duration-700 flex items-center justify-center" data-id="element-7496">
                <CornerOrnaments data-id="element-7497" />
                <div className="w-12 h-12 rounded-full bg-[#8B0000] border-2 border-[#D4AF37] flex items-center justify-center shadow-lg relative z-10" data-id="element-7498">
                  <Crown className="w-5 h-5 text-[#D4AF37]" data-id="element-7499" />
                </div>
                <div className="absolute top-1/2 left-0 w-full h-px bg-[#D4AF37]/30 -translate-y-1/2" data-id="element-7500" />
                <div className="absolute left-1/2 top-0 w-px h-full bg-[#D4AF37]/30 -translate-x-1/2" data-id="element-7501" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Quick Support Options */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-7502">
        <div className="max-w-6xl mx-auto" data-id="element-7503">
          <SectionHeader title="Choose How to Support" data-id="element-7504" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-7505">
            {[{
            title: 'Buy a Court Packet',
            price: 'From $14.99',
            icon: Package,
            copy: 'Open a premium digital packet and reveal portraits, seals, treasures, or memory fragments from the Lahore Treasury.',
            bullets: ['3 collectible reveals', 'Chance of Rare, Epic, or Treasury Rare', 'Adds to your personal collection', 'Includes lore and historical notes'],
            cta: 'Buy Court Packet',
            action: () => openSponsorModal('Court Packet')
          }, {
            title: 'Sponsor a Student',
            price: '$25',
            icon: GraduationCap,
            copy: 'Help a young person experience The Kingdoms of Punjab and receive their first Lahore Treasury collectible.',
            bullets: ['Supports student access', 'Includes one digital Court Packet', 'Optional patron wall recognition', 'Ideal for families and alumni'],
            cta: 'Sponsor a Student',
            action: () => openSponsorModal('Student Access')
          }, {
            title: 'Sponsor a School Bus',
            price: '$1,500–$2,500',
            icon: Bus,
            copy: 'Bring an entire group of students to the live experience and give them a cultural memory they can keep.',
            bullets: ['Supports up to 50 students', 'Includes group Court Packet unlock', 'Recognition as a Bus Patron', 'Optional school dedication'],
            cta: 'Sponsor a Bus',
            action: () => openSponsorModal('School Bus')
          }, {
            title: 'Buy Chaa for the Team',
            price: '$11 / $21 / $51',
            icon: Coffee,
            copy: 'Support the artists, guides, volunteers, and builders bringing the Lahore Treasury to life.',
            bullets: ['Small patron gesture', 'Helps support production days', 'Unlocks a thank-you badge', 'Optional message to the team'],
            cta: 'Buy Chaa',
            action: () => setIsChaaModalOpen(true)
          }].map((card, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group relative bg-[#0B0F19] border border-[#D4AF37]/20 p-8 hover:border-[#D4AF37]/60 transition-all duration-300 flex flex-col" data-id="element-7506">
                <div className="w-12 h-12 rounded-full bg-[#131B2B] border border-[#D4AF37]/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-7507">
                  <card.icon className="w-5 h-5 text-[#D4AF37]" data-id="element-7508" />
                </div>
                <h3 className="font-serif text-xl text-[#FDFBF7] mb-1" data-id="element-7509">
                  {card.title}
                </h3>
                <p className="text-[#D4AF37] font-mono text-sm mb-4" data-id="element-7510">
                  {card.price}
                </p>
                <p className="text-[#FDFBF7]/60 text-sm mb-6 leading-relaxed" data-id="element-7511">
                  {card.copy}
                </p>
                <ul className="space-y-3 mb-8 flex-1" data-id="element-7512">
                  {card.bullets.map((b, j) => <li key={j} className="flex items-start gap-2 text-xs text-[#FDFBF7]/50" data-id="element-7513">
                      <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-1.5 shrink-0" data-id="element-7514" />
                      {b}
                    </li>)}
                </ul>
                <button onClick={card.action} className="w-full py-3 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0F19] transition-colors" data-id="element-7515">
                  {card.cta}
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 3. Impact Calculator */}
      <section className="py-24 px-4 relative" data-id="element-7516">
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-7517">
          <div className="bg-[#131B2B] border border-[#D4AF37]/30 p-8 sm:p-12 relative overflow-hidden" data-id="element-7518">
            <CornerOrnaments data-id="element-7519" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-[radial-gradient(circle_at_top_right,_rgba(212,175,55,0.1),_transparent_70%)]" data-id="element-7520" />

            <div className="text-center mb-10" data-id="element-7521">
              <h2 className="font-serif text-3xl text-[#FDFBF7] mb-3" data-id="element-7522">
                Build Your Impact
              </h2>
              <p className="text-[#FDFBF7]/60 text-sm" data-id="element-7523">
                Choose a contribution and see what it helps unlock.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-7524">
              {[{
              label: '1 Student',
              val: 25
            }, {
              label: '10 Students',
              val: 250
            }, {
              label: '1 School Bus',
              val: 2000
            }, {
              label: 'Chaa',
              val: 21
            }].map(opt => <button key={opt.val} onClick={() => {
              setImpactAmount(opt.val);
              setCustomImpact('');
            }} className={`px-6 py-3 border text-xs tracking-wider uppercase transition-colors ${impactAmount === opt.val ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#D4AF37]/20 text-[#FDFBF7]/60 hover:border-[#D4AF37]/50'}`} data-id="element-7525">
                  {opt.label} (${opt.val})
                </button>)}
              <div className="relative" data-id="element-7526">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#FDFBF7]/40 text-sm" data-id="element-7527">
                  $
                </span>
                <input type="number" placeholder="Custom" value={customImpact} onChange={e => {
                setCustomImpact(e.target.value);
                setImpactAmount(Number(e.target.value) || 0);
              }} className="w-32 bg-[#0B0F19] border border-[#D4AF37]/20 pl-8 pr-4 py-3 text-xs text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/50 transition-colors uppercase tracking-wider" data-id="element-7528" />
              </div>
            </div>

            <div className="text-center mb-10" data-id="element-7529">
              <p className="text-xl sm:text-2xl font-serif text-[#FDFBF7] leading-relaxed" data-id="element-7530">
                Your support helps bring{' '}
                <span className="text-[#D4AF37] text-3xl sm:text-4xl mx-2" data-id="element-7531">
                  {currentImpact.students}
                </span>{' '}
                students into the Lahore Court experience.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 border-t border-[#D4AF37]/15" data-id="element-7532">
              <div className="text-center" data-id="element-7533">
                <p className="text-[#D4AF37] font-serif text-3xl mb-1" data-id="element-7534">
                  {currentImpact.students}
                </p>
                <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.2em] uppercase" data-id="element-7535">
                  Students Supported
                </p>
              </div>
              <div className="text-center" data-id="element-7536">
                <p className="text-[#D4AF37] font-serif text-3xl mb-1" data-id="element-7537">
                  {currentImpact.packets}
                </p>
                <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.2em] uppercase" data-id="element-7538">
                  Packets Unlocked
                </p>
              </div>
              <div className="text-center" data-id="element-7539">
                <p className="text-[#D4AF37] font-serif text-3xl mb-1" data-id="element-7540">
                  {currentImpact.buses > 0 ? currentImpact.buses : '< 1'}
                </p>
                <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.2em] uppercase" data-id="element-7541">
                  Buses Funded
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Sponsor a Collection */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-7542">
        <div className="max-w-6xl mx-auto" data-id="element-7543">
          <SectionHeader title="Sponsor a Collection" subtitle="Help preserve and present one part of the Lahore Treasury." data-id="element-7544" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-7545">
            {[{
            name: 'Lahore Court Collection',
            desc: 'Founding faces of sovereignty.',
            price: '$5,000',
            sponsoredBy: 'Akal Steel',
            isFlagship: true
          }, {
            name: 'Founders’ Seals',
            desc: 'Authority made visible.',
            price: '$7,500'
          }, {
            name: 'Court Treasures',
            desc: 'Jewels, regalia, and royal objects.',
            price: '$10,000'
          }, {
            name: 'Royal Women & Chamber Culture',
            desc: 'The inner court of counsel, lineage, music, and refinement.',
            price: '$7,500'
          }, {
            name: 'Sacred Authority',
            desc: 'Reverence before rule.',
            price: '$10,000'
          }, {
            name: 'Frontier Defenders',
            desc: 'Martial prestige at the empire’s edge.',
            price: '$7,500'
          }].map((col: any, i: number) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`group relative bg-[#0B0F19] border p-8 transition-all duration-300 ${col.sponsoredBy ? 'border-[#D4AF37]/50 shadow-[0_0_30px_rgba(212,175,55,0.08)]' : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/60'}`} data-id="element-7546">
                {col.sponsoredBy && <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-7547" />}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.05),_transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-7548" />
                {col.isFlagship && <p className="text-[#D4AF37] text-[9px] tracking-[0.3em] uppercase font-bold mb-2 relative z-10" data-id="element-7549">
                    Flagship Collection
                  </p>}
                <h3 className="font-serif text-xl text-[#FDFBF7] mb-2 relative z-10" data-id="element-7550">
                  {col.name}
                </h3>
                <p className="text-[#FDFBF7]/60 text-sm mb-6 h-10 relative z-10" data-id="element-7551">
                  {col.desc}
                </p>
                {col.sponsoredBy ? <>
                    <div className="pt-6 border-t border-[#D4AF37]/30 mb-6 relative z-10" data-id="element-7552">
                      <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-1.5" data-id="element-7553">
                        Presented in partnership with
                      </p>
                      <p className="font-serif text-xl text-[#D4AF37] leading-none" data-id="element-7554">
                        {col.sponsoredBy}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-[10px] mt-2 italic" data-id="element-7555">
                        Sponsorship secured.
                      </p>
                    </div>
                    <button onClick={() => openSponsorModal('Collection')} className="w-full py-3 bg-[#D4AF37]/8 border border-[#D4AF37]/40 text-[#D4AF37]/85 text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37]/15 transition-colors relative z-10" data-id="element-7556">
                      Inquire About Other Tiers
                    </button>
                  </> : <>
                    <div className="pt-6 border-t border-[#D4AF37]/15 mb-6 relative z-10" data-id="element-7557">
                      <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase mb-1" data-id="element-7558">
                        Starting From
                      </p>
                      <p className="text-[#D4AF37] font-mono text-lg" data-id="element-7559">
                        {col.price}
                      </p>
                    </div>
                    <button onClick={() => openSponsorModal('Collection')} className="w-full py-3 bg-[#131B2B] border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0F19] transition-colors relative z-10" data-id="element-7560">
                      Become a Patron
                    </button>
                  </>}
              </motion.div>)}
          </div>
          <p className="text-center text-[#FDFBF7]/30 text-[10px] max-w-2xl mx-auto uppercase tracking-wider" data-id="element-7561">
            Collection sponsors may receive recognition on the website, event
            materials, patron wall, and selected digital collectible pages.
            Final recognition is subject to cultural and editorial approval.
          </p>
        </div>
      </section>

      {/* 5. Patron Tiers */}
      <section className="py-24 px-4" data-id="element-7562">
        <div className="max-w-6xl mx-auto" data-id="element-7563">
          <SectionHeader title="Patron Tiers" subtitle="For families, businesses, foundations, and community leaders who want to help build the Lahore Treasury." data-id="element-7564" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-7565">
            {[{
            tier: 'Friend of the Court',
            price: '$101',
            benefits: ['Name on digital patron wall', '1 supporter badge', '1 Court Packet'],
            cta: 'Become a Friend',
            type: 'Other'
          }, {
            tier: 'Darbar Patron',
            price: '$501',
            benefits: ['Patron wall recognition', '5 Court Packets', 'Early access to selected drops', 'Invitation to patron preview'],
            cta: 'Become a Patron',
            type: 'Other'
          }, {
            tier: 'Treasury Patron',
            price: '$2,500',
            benefits: ['Sponsor student access', '15 Court Packets', 'Recognition on selected collection page', 'Optional family dedication'],
            cta: 'Become a Treasury Patron',
            type: 'Other'
          }, {
            tier: 'Founding Patron',
            price: '$10,000+',
            benefits: ['Major recognition', 'Collection sponsorship eligibility', 'Private preview experience', 'Founder’s Edition digital collectible', 'Optional commemorative coin eligibility'],
            cta: 'Apply as Founding Patron',
            type: 'Founding Partner',
            isPremium: true
          }].map((tier, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`relative p-8 flex flex-col ${tier.isPremium ? 'bg-gradient-to-b from-[#131B2B] to-[#0B0F19] border border-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'bg-[#131B2B]/40 border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors'}`} data-id="element-7566">
                {tier.isPremium && <CornerOrnaments data-id="element-7567" />}
                <h3 className={`font-serif text-xl mb-2 ${tier.isPremium ? 'text-[#D4AF37]' : 'text-[#FDFBF7]'}`} data-id="element-7568">
                  {tier.tier}
                </h3>
                <p className="font-mono text-lg text-[#FDFBF7]/80 mb-6 pb-6 border-b border-[#D4AF37]/15" data-id="element-7569">
                  {tier.price}
                </p>
                <ul className="space-y-4 mb-8 flex-1" data-id="element-7570">
                  {tier.benefits.map((b, j) => <li key={j} className="flex items-start gap-3 text-sm text-[#FDFBF7]/70" data-id="element-7571">
                      <Shield className={`w-4 h-4 shrink-0 mt-0.5 ${tier.isPremium ? 'text-[#D4AF37]' : 'text-[#D4AF37]/50'}`} data-id="element-7572" />
                      <span className="leading-tight" data-id="element-7573">{b}</span>
                    </li>)}
                </ul>
                <button onClick={() => openSponsorModal(tier.type)} className={`w-full py-3 text-[10px] tracking-[0.2em] uppercase transition-colors ${tier.isPremium ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] font-bold' : 'border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0F19]'}`} data-id="element-7574">
                  {tier.cta}
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 6. Where Your Support Goes */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-7575">
        <div className="max-w-5xl mx-auto" data-id="element-7576">
          <SectionHeader title="Where Your Support Goes" data-id="element-7577" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-7578">
            {[{
            icon: Users,
            label: 'Student Access'
          }, {
            icon: Bus,
            label: 'School Buses'
          }, {
            icon: Camera,
            label: 'Artists & Designers'
          }, {
            icon: BookOpen,
            label: 'Historical Research'
          }, {
            icon: Globe,
            label: 'Live Experience Production'
          }, {
            icon: Package,
            label: 'Digital Collectible Platform'
          }, {
            icon: Heart,
            label: 'Community Programming'
          }, {
            icon: Shield,
            label: 'Preservation of Heritage'
          }].map((item, i) => <div key={i} className="flex flex-col items-center" data-id="element-7579">
                <div className="w-12 h-12 rounded-full border border-[#D4AF37]/20 bg-[#0B0F19] flex items-center justify-center mb-4" data-id="element-7580">
                  <item.icon className="w-5 h-5 text-[#D4AF37]/70" data-id="element-7581" />
                </div>
                <p className="text-[#FDFBF7]/80 text-xs uppercase tracking-wider leading-relaxed max-w-[120px]" data-id="element-7582">
                  {item.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Sponsor Opportunities */}
      <section className="py-24 px-4" data-id="element-7583">
        <div className="max-w-6xl mx-auto" data-id="element-7584">
          <div className="text-center mb-16" data-id="element-7585">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-7586">
              For Sponsors
            </h2>
            <p className="text-[#D4AF37] text-lg font-serif italic mb-4" data-id="element-7587">
              Sponsor a cultural moment, not just a logo placement.
            </p>
            <p className="text-[#FDFBF7]/60 text-sm max-w-2xl mx-auto" data-id="element-7588">
              Lahore Treasury gives sponsors the ability to support student
              access, live cultural programming, digital collectibles,
              commemorative rewards, and community recognition.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7589">
            {[{
            title: 'Student Access Sponsor',
            price: 'Custom',
            bestFor: 'Families, Foundations'
          }, {
            title: 'School Bus Sponsor',
            price: 'From $1,500',
            bestFor: 'Businesses, Community Groups'
          }, {
            title: 'Collection Sponsor',
            price: 'From $5,000',
            bestFor: 'Families, Businesses'
          }, {
            title: 'Treasury Night Sponsor',
            price: 'Custom',
            bestFor: 'Corporate Sponsors'
          }, {
            title: 'Commemorative Coin Sponsor',
            price: 'Custom',
            bestFor: 'Elected Officials, Businesses'
          }, {
            title: 'Founding Partner',
            price: '$10,000+',
            bestFor: 'Major Philanthropists'
          }].map((opp, i) => <div key={i} className="bg-[#131B2B]/60 border border-[#D4AF37]/20 p-6 flex flex-col hover:border-[#D4AF37]/50 transition-colors" data-id="element-7590">
                <h3 className="font-serif text-lg text-[#FDFBF7] mb-1" data-id="element-7591">
                  {opp.title}
                </h3>
                <p className="text-[#D4AF37] font-mono text-sm mb-4" data-id="element-7592">
                  {opp.price}
                </p>
                <div className="mt-auto pt-4 border-t border-[#D4AF37]/10 mb-6" data-id="element-7593">
                  <p className="text-[#FDFBF7]/40 text-[10px] tracking-widest uppercase mb-1" data-id="element-7594">
                    Best For
                  </p>
                  <p className="text-[#FDFBF7]/80 text-xs" data-id="element-7595">{opp.bestFor}</p>
                </div>
                <button onClick={() => openSponsorModal(opp.title)} className="w-full py-2.5 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0F19] transition-colors" data-id="element-7596">
                  Request Sponsor Info
                </button>
              </div>)}
          </div>
        </div>
      </section>

      {/* 8. FAQ Section */}
      <section className="py-24 px-4 bg-[#131B2B]/40 border-y border-[#D4AF37]/10" data-id="element-7597">
        <div className="max-w-3xl mx-auto" data-id="element-7598">
          <SectionHeader title="Questions Before You Support?" data-id="element-7599" />
          <div className="space-y-4" data-id="element-7600">
            {[{
            q: 'Is this an NFT project?',
            a: 'No. Lahore Treasury uses digital collectibles for cultural storytelling, event access, recognition, and collection-building. It should not be presented as a crypto or investment product.'
          }, {
            q: 'Can I sponsor a specific school?',
            a: 'Yes, where operationally possible. Sponsors can request a school, region, or community group.'
          }, {
            q: 'Can my family or business be recognized?',
            a: 'Yes. Recognition options may include the patron wall, selected collection pages, event materials, or sponsor acknowledgements, subject to cultural and editorial approval.'
          }, {
            q: 'Can I make a custom contribution?',
            a: 'Yes. Custom support is available for patrons, sponsors, families, and foundations.'
          }, {
            q: 'What does "Buy Chaa for the Team" mean?',
            a: 'It is a small contribution that supports the artists, guides, volunteers, and builders working behind the scenes.'
          }].map((faq, i) => {
            const [isOpen, setIsOpen] = useState(false);
            return <div key={i} className="border border-[#D4AF37]/20 bg-[#0B0F19]" data-id="element-7601">
                  <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 flex items-center justify-between text-left" data-id="element-7602">
                    <span className="font-serif text-lg text-[#FDFBF7] pr-4" data-id="element-7603">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-[#D4AF37] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-7604" />
                  </button>
                  <AnimatePresence data-id="element-7605">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-7606">
                        <div className="px-6 pb-5 text-[#FDFBF7]/60 text-sm leading-relaxed border-t border-[#D4AF37]/10 pt-4" data-id="element-7607">
                          {faq.a}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* 9. Final CTA */}
      <section className="py-32 px-4 relative overflow-hidden" data-id="element-7608">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(212,175,55,0.08),_transparent_60%)]" data-id="element-7609" />
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-7610">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-7611">
            Help Open the Lahore Court
          </h2>
          <p className="text-[#FDFBF7]/60 text-lg mb-12" data-id="element-7612">
            Support a student, fund a bus, sponsor a collection, or become a
            patron of the Treasury.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7613">
            <button onClick={() => openSponsorModal('Student Access')} className="w-full sm:w-auto px-8 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#E5C158] transition-colors" data-id="element-7614">
              Sponsor a Student
            </button>
            <button onClick={() => openSponsorModal('Founding Partner')} className="w-full sm:w-auto px-8 py-4 border border-[#D4AF37]/40 text-[#D4AF37] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#D4AF37]/10 transition-colors" data-id="element-7615">
              Become a Patron
            </button>
            <button onClick={() => openSponsorModal('Other')} className="w-full sm:w-auto px-8 py-4 border border-[#FDFBF7]/20 text-[#FDFBF7] font-bold tracking-[0.2em] uppercase text-xs hover:bg-[#FDFBF7]/10 transition-colors" data-id="element-7616">
              Request Sponsor Info
            </button>
          </div>
        </div>
      </section>

      {/* 10. Footer */}
      <footer className="px-4 py-16 bg-[#0B0F19] border-t border-[#D4AF37]/20 text-center" data-id="element-7617">
        <div className="max-w-4xl mx-auto" data-id="element-7618">
          <OrnateDivider className="mb-10" data-id="element-7619" />

          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 mb-12" data-id="element-7620">
            <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-7621">
              Treasury Home
            </Link>
            <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-7622">
              Collections
            </Link>
            <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-7623">
              Court Packet
            </Link>
            <Link to="/support-the-court" className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-7624">
              Support the Court
            </Link>
            <a href="#" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-7625">
              Contact
            </a>
          </div>

          <p className="text-[#D4AF37] font-serif text-xl tracking-[0.3em] uppercase mb-3" data-id="element-7626">
            Lahore Treasury
          </p>
          <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.25em] uppercase font-light" data-id="element-7627">
            Enter the Lahore Court
          </p>
        </div>
      </footer>

      {/* Modals Mounted at Root */}
      <BuyChaaModal isOpen={isChaaModalOpen} onClose={() => setIsChaaModalOpen(false)} data-id="element-7628" />
      <SponsorInquiryModal isOpen={isSponsorModalOpen} onClose={() => setIsSponsorModalOpen(false)} defaultType={sponsorModalType} data-id="element-7629" />
    </div>;
};